import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TenantService from "../../services/tenantService";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import TenantLayout from "../../layouts/TenantLayout";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

export default function TenantsPatientProfile() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('tenantDashboard');
  const tenantService = new TenantService();
  const token = localStorage.getItem("tokenTenant");
  const { id } = useParams();
  const [patientInfo, setPatientInfo] = useState(null);

  useEffect(() => {
    const fetchPatientInfo = async () => {
      try {
        const response = await tenantService.getPatinetInfo(id, token);
        console.log("TenantsPatientProfile - Full Response:", response);
        console.log("TenantsPatientProfile - Patient Info:", response?.patient);
        console.log("TenantsPatientProfile - totalInvitationCount:", response?.patient?.totalInvitationCount);
        console.log("TenantsPatientProfile - Forms:", response?.patient?.Forms);
        if (response?.patient?.Forms) {
          response.patient.Forms.forEach((form, index) => {
            console.log(`Form ${index + 1} (${form.name}):`, {
              id: form.id,
              invitationCount: form.invitationCount,
              invitations: form.invitations,
              invitationsLength: form.invitations?.length
            });
          });
        }
        setPatientInfo(response?.patient ?? null);
      } catch (error) {
        console.error("Error fetching patient info:", error);
      }
    };

    if (id && token) {
      fetchPatientInfo();
    }
  }, [id, token]);

  const fullName = useMemo(() => {
    if (!patientInfo) return t('patientOverview').split(' ')[0]; // "Patient" from "Patient Overview"
    const first = patientInfo.firstName || patientInfo.name || "";
    const last = patientInfo.lastName || patientInfo.surname || "";
    const combined = `${first} ${last}`.trim();
    return combined || t('patientOverview').split(' ')[0];
  }, [patientInfo, t]);

  const doctorName = useMemo(() => {
    if (!patientInfo) return "—";
    if (patientInfo.doctorName) return patientInfo.doctorName;
    if (patientInfo.Doctor) {
      const doctor = patientInfo.Doctor;
      return `${doctor.name ?? ""} ${doctor.surname ?? ""}`.trim() || "—";
    }
    return "—";
  }, [patientInfo]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return "—";
    return parsed.toLocaleDateString();
  };

  const primaryPhone = patientInfo?.primaryPhone || patientInfo?.phone_number || "—";
  const secondaryPhone = patientInfo?.secondaryPhone || patientInfo?.secondary_phone || "—";
  const patientFiles = patientInfo?.Files ?? [];
  const fileName =
    patientFiles.length === 1
      ? patientFiles[0].name
      : patientFiles.length > 1
      ? `${patientFiles.length} Files`
      : patientInfo?.file || patientInfo?.fileName || patientInfo?.file_name || "—";
  const email = patientInfo?.email || "—";
  const gender = patientInfo?.gender || "—";
  const rawDateOfBirth = patientInfo?.dateOfBirth || patientInfo?.birthDate || "";
  const dateOfBirth = formatDate(rawDateOfBirth);
  const forms = patientInfo?.Forms ?? [];
  const totalForms = patientInfo?.totalFormCount ?? forms.length ?? 0;
  // Calculate totalInvitations: use backend's totalInvitationCount if available,
  // otherwise sum up invitationCount from each form, or fallback to invitations array length
  const totalInvitations = patientInfo?.totalInvitationCount ??
    forms.reduce((acc, form) => {
      // Prefer invitationCount (from backend) over invitations array length
      if (form.invitationCount !== undefined && form.invitationCount !== null) {
        return acc + form.invitationCount;
      }
      // Fallback to invitations array length
      return acc + (form.invitations?.length ?? 0);
    }, 0);
  const totalAnswers = patientInfo?.totalFormAnswersCount ?? patientInfo?.overallScore?.totalAnsweredForms ?? 0;

  const age = useMemo(() => {
    if (!rawDateOfBirth) return "—";
    const dob = new Date(rawDateOfBirth);
    if (Number.isNaN(dob.getTime())) return "—";
    const diff = Date.now() - dob.getTime();
    const ageDate = new Date(diff);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }, [rawDateOfBirth]);

  const summaryCards = useMemo(
    () => [
      {
        label: t('patientAge'),
        value: age === "—" ? "—" : `${age} ${t('yrs')}`,
        sublabel: dateOfBirth === "—" ? t('birthDateUnavailable') : `${t('born')} ${dateOfBirth}`,
        icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
      },
      {
        label: t('linkedFiles'),
        value: patientFiles.length,
        sublabel: patientFiles.length === 1 ? t('fileConnected') : t('filesAssociated'),
        icon: <FolderSharedOutlinedIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.blueAccent[700]} 100%)`,
      },
      {
        label: t('invitations'),
        value: totalInvitations,
        sublabel: `${totalForms} ${t('formsAvailable')}`,
        icon: <SendOutlinedIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
      },
    ],
    [age, colors, dateOfBirth, patientFiles.length, totalForms, totalInvitations, t]
  );

  /** Linked Files / Form Activity: eşit kart yüksekliği + içeride kaydırma */
  const activityPanelCardSx = useMemo(
    () => ({
      borderRadius: 4,
      flex: 1,
      width: "100%",
      minHeight: { xs: 280, md: 380 },
      display: "flex",
      flexDirection: "column",
      backgroundColor: colors.primary[500],
      color: colors.grey[100],
      boxShadow: `0 20px 40px rgba(15, 23, 42, 0.28)`,
      border: `1px solid ${colors.primary[300]}55`,
      overflow: "hidden",
    }),
    [colors]
  );

  const activityListScrollSx = useMemo(
    () => ({
      flex: 1,
      minHeight: { xs: 160, md: 200 },
      maxHeight: { xs: "min(42vh, 300px)", sm: "min(46vh, 380px)", md: "min(48vh, 440px)" },
      overflowY: "auto",
      overflowX: "hidden",
      borderRadius: 2,
      backgroundColor: `${colors.primary[600]}35`,
      border: `1px solid ${colors.primary[400]}40`,
      px: 1.25,
      py: 0.5,
      "&::-webkit-scrollbar": { width: 8 },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: `${colors.blueAccent[700]}66`,
        borderRadius: 4,
      },
    }),
    [colors]
  );

  return (
    <TenantLayout
      contentSx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        backgroundColor: "#f5f7fb",
        pb: { xs: 6, md: 8 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          margin: "0 auto",
          maxWidth: "1100px",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Card
          sx={{
            borderRadius: 4,
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.blueAccent[800]} 100%)`,
            color: colors.grey[100],
            boxShadow: `0 24px 48px rgba(15, 23, 42, 0.35)`,
            overflow: "hidden",
          }}
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
            }}
          >
            <Stack
              alignItems="center"
              spacing={2.5}
              sx={{
                flexShrink: 0,
                minWidth: { xs: "auto", md: 260 },
              }}
            >
              <Avatar
                src={`../../assets/avatarPhoto.jpg`}
                alt="Patient Avatar"
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: "32px",
                  boxShadow: `0 16px 40px rgba(15, 23, 42, 0.35)`,
                  border: `4px solid rgba(255,255,255,0.2)`
                }}
              />
              <Box textAlign="center">
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {fullName}
                </Typography>
                <Chip
                  label={doctorName === "—" ? t('unassigned') : t('assigned')}
                  sx={{
                    mt: 1.5,
                    px: 2.5,
                    py: 0.5,
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    color: colors.grey[100],
                    background:
                      doctorName === "—"
                        ? `${colors.grey[700]}aa`
                        : `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
                    boxShadow:
                      doctorName === "—"
                        ? `0 10px 24px rgba(148, 163, 184, 0.35)`
                        : `0 10px 24px rgba(34, 197, 94, 0.45)`,
                  }}
                />
              </Box>
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: `${colors.primary[300]}55`,
                borderWidth: 1,
                display: { xs: "none", md: "block" },
              }}
            />

            <Grid container spacing={3} sx={{ flexGrow: 1 }}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ color: `${colors.grey[100]}99`, letterSpacing: 1 }}
                    >
                      {t('contact')}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" mt={0.8}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.blueAccent[500]}88`,
                        }}
                      >
                        <EmailOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          {t('columnEmail')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {email}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.blueAccent[500]}88`,
                        }}
                      >
                        <PhoneIphoneOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          {t('primaryPhone')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {primaryPhone}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.blueAccent[500]}88`,
                        }}
                      >
                        <PhoneIphoneOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          {t('secondaryPhone')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {secondaryPhone}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ color: `${colors.grey[100]}99`, letterSpacing: 1 }}
                    >
                      {t('profile')}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" mt={0.8}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.greenAccent[500]}88`,
                        }}
                      >
                        <WcOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          {t('columnGender')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {gender}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.greenAccent[500]}88`,
                        }}
                      >
                        <CalendarMonthOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          {t('dateOfBirth')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {dateOfBirth}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.greenAccent[500]}88`,
                        }}
                      >
                        <LocalHospitalOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          {t('assignedDoctor')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {doctorName}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.greenAccent[500]}88`,
                        }}
                      >
                        <FolderSharedOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          {t('linkedFile')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {fileName}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {summaryCards.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.label}>
              <Card
                sx={{
                  borderRadius: 4,
                  height: "100%",
                  background: item.gradient,
                  color: colors.grey[100],
                  boxShadow: `0 22px 44px rgba(15, 23, 42, 0.3)`,
                }}
              >
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: "rgba(255,255,255,0.25)",
                        color: "white",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      {item.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ opacity: 0.85 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography variant="body2" sx={{ opacity: 0.75 }}>
                    {item.sublabel}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card sx={activityPanelCardSx}>
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minHeight: 0,
                  py: 3,
                  "&:last-child": { pb: 3 },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                  sx={{ flexShrink: 0 }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 26 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t("linkedFiles")}
                  </Typography>
                  <Chip
                    label={t("linkedFilesCount", { count: patientFiles.length })}
                    size="small"
                    sx={{
                      ml: { xs: 0, sm: "auto" },
                      backgroundColor: `${colors.blueAccent[600]}66`,
                      color: colors.grey[100],
                      fontWeight: 600,
                    }}
                  />
                </Stack>

                <Divider sx={{ borderColor: `${colors.primary[400]}55`, flexShrink: 0 }} />

                <Box sx={activityListScrollSx}>
                  {patientFiles.length === 0 ? (
                    <Box
                      sx={{
                        minHeight: { xs: 140, md: 180 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 2,
                        py: 3,
                      }}
                    >
                      <Typography variant="body2" sx={{ opacity: 0.75, textAlign: "center" }}>
                        {t("noLinkedFiles")}
                      </Typography>
                    </Box>
                  ) : (
                    <List dense disablePadding sx={{ py: 0.5 }}>
                      {patientFiles.map((file) => (
                        <ListItem
                          key={file.id}
                          alignItems="flex-start"
                          sx={{
                            px: 0.5,
                            py: 1.25,
                            borderBottom: `1px solid ${colors.primary[400]}40`,
                            "&:last-of-type": { borderBottom: "none" },
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 48, mt: 0.25 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: `${colors.blueAccent[500]}99`,
                                color: colors.grey[100],
                              }}
                            >
                              <FolderSharedOutlinedIcon fontSize="small" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                                {file.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ opacity: 0.75, display: "block", mt: 0.35 }}>
                                {t("created")} {formatDate(file.createdAt)}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card sx={activityPanelCardSx}>
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minHeight: 0,
                  py: 3,
                  "&:last-child": { pb: 3 },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                  sx={{ flexShrink: 0 }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 26 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t("formActivity")}
                  </Typography>
                  <Chip
                    label={t("formsCount", { count: totalForms })}
                    size="small"
                    sx={{
                      ml: { xs: 0, sm: "auto" },
                      backgroundColor: `${colors.greenAccent[500]}66`,
                      color: colors.grey[100],
                      fontWeight: 600,
                    }}
                  />
                </Stack>

                <Divider sx={{ borderColor: `${colors.primary[400]}55`, flexShrink: 0 }} />

                <Box sx={activityListScrollSx}>
                  {forms.length === 0 ? (
                    <Box
                      sx={{
                        minHeight: { xs: 140, md: 180 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 2,
                        py: 3,
                      }}
                    >
                      <Typography variant="body2" sx={{ opacity: 0.75, textAlign: "center" }}>
                        {t("noFormsAssigned")}
                      </Typography>
                    </Box>
                  ) : (
                    <List dense disablePadding sx={{ py: 0.5 }}>
                      {forms.map((form) => {
                        const invitations = form.invitations ?? [];
                        const latestInvitation = invitations[0];
                        return (
                          <ListItem
                            key={form.id}
                            alignItems="flex-start"
                            sx={{
                              px: 0.5,
                              py: 1.25,
                              borderBottom: `1px solid ${colors.primary[400]}40`,
                              "&:last-of-type": { borderBottom: "none" },
                            }}
                          >
                            <ListItemAvatar sx={{ minWidth: 48, mt: 0.25 }}>
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  backgroundColor: `${colors.greenAccent[500]}99`,
                                  color: colors.grey[900],
                                }}
                              >
                                <AssignmentTurnedInOutlinedIcon fontSize="small" />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                                  {form.name}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 0.35 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.8, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                                  >
                                    {form.description || "—"}
                                  </Typography>
                                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                    <Chip
                                      label={form.type || "unknown"}
                                      size="small"
                                      sx={{
                                        textTransform: "capitalize",
                                        backgroundColor: `${colors.blueAccent[600]}55`,
                                        color: colors.grey[100],
                                        height: 24,
                                        fontSize: "0.7rem",
                                      }}
                                    />
                                    {latestInvitation && (
                                      <Chip
                                        label={`${t("status")}: ${latestInvitation.status}`}
                                        size="small"
                                        sx={{
                                          textTransform: "capitalize",
                                          backgroundColor: `${colors.greenAccent[500]}55`,
                                          color: colors.grey[100],
                                          height: 24,
                                          fontSize: "0.7rem",
                                        }}
                                      />
                                    )}
                                    {latestInvitation?.expires_at && (
                                      <Chip
                                        label={`${t("expires")} ${formatDate(latestInvitation.expires_at)}`}
                                        size="small"
                                        sx={{
                                          backgroundColor: `${colors.redAccent?.[400] ?? "#ef4444"}55`,
                                          color: colors.grey[100],
                                          height: 24,
                                          fontSize: "0.7rem",
                                        }}
                                      />
                                    )}
                                  </Stack>
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>

                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ flexShrink: 0, pt: 0.5, gap: 1 }}
                >
                  <Chip
                    icon={<SendOutlinedIcon sx={{ fontSize: 16 }} />}
                    label={t("totalInvitations", { count: totalInvitations })}
                    size="small"
                    sx={{
                      backgroundColor: `${colors.blueAccent[600]}55`,
                      color: colors.grey[100],
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    icon={<AssessmentOutlinedIcon sx={{ fontSize: 16 }} />}
                    label={t("completed", { count: totalAnswers })}
                    size="small"
                    sx={{
                      backgroundColor: `${colors.greenAccent[500]}55`,
                      color: colors.grey[100],
                      fontWeight: 600,
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </TenantLayout>
  );
}
